import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { cleanContactFields, type ContactInput } from "@/hooks/use-customer-contacts";
import type { LeadDuplicate, ManualLeadCompany } from "@/lib/manual-lead";

/** Finnes org.nr. som kunde eller lead fra før (RPC check_lead_duplicate)? null = ledig. */
export function useLeadDuplicate(organizationNumber: string | null) {
  return useQuery({
    queryKey: ["lead-duplicate", organizationNumber],
    queryFn: async (): Promise<LeadDuplicate | null> => {
      const { data, error } = await supabase.rpc("check_lead_duplicate", { p_organization_number: organizationNumber });
      if (error) throw new Error(error.message);
      return (data as LeadDuplicate | null) ?? null;
    },
    enabled: !!organizationNumber,
    staleTime: 0,
  });
}

export interface CreateManualLeadInput {
  company: ManualLeadCompany;
  contact?: ContactInput | null;
  /** Bare for de med Leadlister; ellers havner leadet i «Manuelle». */
  listId?: string | null;
  /** «Jeg ringer selv»: leadet kommer som ditt neste. */
  reserve: boolean;
}

export interface CreateManualLeadResult {
  lead_id: string;
  list_id: string;
  list_name: string;
  name: string;
}

/**
 * Legger inn ett lead for hånd (RPC create_manual_lead). Databasen sjekker duplikater på nytt
 * og skriver lead.created_manual med innlogget bruker som aktør.
 */
export function useCreateManualLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateManualLeadInput): Promise<CreateManualLeadResult> => {
      const contact = input.contact?.full_name.trim() ? cleanContactFields(input.contact) : null;
      const { data, error } = await supabase.rpc("create_manual_lead", {
        p_company: input.company,
        p_contact: contact,
        p_list_id: input.listId ?? null,
        p_reserve: input.reserve,
      });
      if (error) throw new Error(error.message);
      return data as CreateManualLeadResult;
    },
    onSuccess: async (res) => {
      toast.success(`${res.name} er lagt til i ${res.list_name}`);
      await invalidateFor(qc, "leads");
    },
    onError: (err: Error) => toast.error(`Kunne ikke legge til lead: ${err.message}`),
  });
}
